import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, PackagePlus, SearchCheck, MessageSquare } from 'lucide-react';
import { API_BASE } from '@/lib/api-base';
import { useStorefront } from '@/context/StorefrontContext';
import { useSurfaceAccess } from '@/context/SurfaceAccessContext';

type RequestType = 'booking' | 'rfq' | 'inquiry';

type RequestConfig = {
  requestType: RequestType;
  title: string;
  description: string;
  submitLabel: string;
  icon: typeof PackagePlus;
  fields: Array<{ key: string; label: string; placeholder: string; type?: 'text' | 'textarea' }>;
};

const CONFIG: Record<RequestType, RequestConfig> = {
  booking: {
    requestType: 'booking',
    title: 'Book Shipment',
    description: 'Capture a verified shipment booking request from a known contact.',
    submitLabel: 'Submit Booking Request',
    icon: PackagePlus,
    fields: [
      { key: 'cargoType', label: 'Cargo Type', placeholder: 'FCL / LCL / Air' },
      { key: 'origin', label: 'Origin', placeholder: 'Origin port / city' },
      { key: 'destination', label: 'Destination', placeholder: 'Destination port / city' },
      { key: 'details', label: 'Shipment Details', placeholder: 'Commodity, weight, ready date, quantity...', type: 'textarea' },
    ],
  },
  rfq: {
    requestType: 'rfq',
    title: 'Sales Inquiry / RFQ',
    description: 'Collect a verified sales lead and keep it linked to the tenant relationship.',
    submitLabel: 'Submit RFQ',
    icon: SearchCheck,
    fields: [
      { key: 'tradeLane', label: 'Trade Lane', placeholder: 'e.g. Singapore → Jakarta' },
      { key: 'serviceType', label: 'Service Type', placeholder: 'FCL / LCL / Air / Warehousing' },
      { key: 'targetVolume', label: 'Target Volume', placeholder: 'Monthly volume / expected shipments' },
      { key: 'details', label: 'RFQ Brief', placeholder: 'Tell us your requirement, timing, and special needs...', type: 'textarea' },
    ],
  },
  inquiry: {
    requestType: 'inquiry',
    title: 'Other Inquiry',
    description: 'Route general enquiries from known, verified company contacts.',
    submitLabel: 'Send Inquiry',
    icon: MessageSquare,
    fields: [
      { key: 'subject', label: 'Subject', placeholder: 'What do you need help with?' },
      { key: 'details', label: 'Message', placeholder: 'Describe your question or request...', type: 'textarea' },
    ],
  },
};

export function SurfaceRequestPage({ requestType }: { requestType: RequestType }) {
  const config = CONFIG[requestType];
  const Icon = config.icon;
  const { host, brand } = useStorefront();
  const { ensureAccess, buildHeaders } = useSurfaceAccess();
  const navigate = useNavigate();
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    const allowed = await ensureAccess(requestType);
    if (!allowed) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/selfservice/surface/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...buildHeaders(),
        },
        body: JSON.stringify({
          host,
          requestType,
          title: values.subject || values.tradeLane || values.origin || config.title,
          message: values.details || values.destination || 'Surface request submitted',
          details: values,
        }),
      });
      const body = await res.json() as { ok: boolean; data?: { message?: string }; error?: { message?: string } };
      if (!res.ok || !body.ok) throw new Error(body.error?.message ?? 'Unable to submit request');
      setSuccess(body.data?.message ?? 'Request submitted');
      setValues({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-14 animate-fade-in">
      <button
        type="button"
        onClick={() => navigate('/')}
        className="mb-6 text-sm text-slate-500 hover:text-brand-600"
      >
        ← Back to home
      </button>

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{config.title}</h1>
              <p className="mt-1 text-sm text-slate-500">{config.description}</p>
              <p className="mt-2 text-xs text-slate-400">Verified requests are linked to your company and {brand.brandShortName}.</p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5 px-6 py-6">
          <div className="grid gap-5 md:grid-cols-2">
            {config.fields.map((field) => (
              <label key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                <span className="mb-1 block text-xs font-medium text-slate-600">{field.label}</span>
                {field.type === 'textarea' ? (
                  <textarea
                    rows={5}
                    value={values[field.key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none"
                  />
                ) : (
                  <input
                    value={values[field.key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none"
                  />
                )}
              </label>
            ))}
          </div>

          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          {success && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{success}</div>}

          <div className="flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs text-slate-400">Email OTP verification is required before submission.</div>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-700 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-brand-800 disabled:opacity-50"
            >
              {config.submitLabel}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
